import { useEffect, useState, useCallback } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import {
  Warehouse, Plus, Trash2, Loader2, Copy, Download, KeyRound, Link2, Building2, QrCode, Check, X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { api } from "../lib/api";

const linkFor = (token) => `${window.location.origin}/warehouse/${token}`;

const CredentialCard = ({ wh, password, onClose }) => {
  const [qr, setQr] = useState("");
  const link = linkFor(wh.access_token);
  useEffect(() => { QRCode.toDataURL(link, { width: 240, margin: 1 }).then(setQr).catch(() => {}); }, [link]);
  const copy = (txt, label) => { navigator.clipboard.writeText(txt); toast.success(`تم نسخ ${label}`); };
  const download = () => {
    const html = `<!doctype html><html dir="rtl"><head><meta charset="utf-8"><title>بطاقة دخول ${wh.name}</title>
<style>body{font-family:Tahoma,Arial;padding:40px;text-align:center;color:#0f172a}
.box{max-width:420px;margin:auto;border:1px solid #e2e8f0;border-radius:16px;padding:28px}
h1{font-size:20px}img{width:220px;height:220px}
.row{margin:10px 0;font-size:15px}.k{color:#64748b;font-size:12px}
.v{direction:ltr;font-weight:bold;word-break:break-all}a{color:#2563eb}</style></head>
<body><div class="box"><h1>🏬 ${wh.name}</h1><p>${wh.city || ""}</p>
${qr ? `<img src="${qr}"/>` : ""}
<div class="row"><div class="k">رابط المستودع</div><div class="v"><a href="${link}">${link}</a></div></div>
${password ? `<div class="row"><div class="k">كلمة المرور</div><div class="v">${password}</div></div>` : ""}
<p style="color:#64748b;font-size:12px">افتح الرابط على الجوال وسجّل الدخول لصرف قطع الغيار.</p></div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `بطاقة-${wh.name}.html`; a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3" data-testid="wh-credential-card">
      <div className="flex items-center justify-between">
        <span className="font-heading font-bold text-emerald-800 flex items-center gap-1.5"><Check className="h-4 w-4" /> تم إنشاء المستودع</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
      </div>
      {qr && <img src={qr} alt="qr" className="h-36 w-36 mx-auto rounded-lg bg-white p-1 border" data-testid="wh-qr" />}
      <div className="space-y-1">
        <Label className="text-xs">رابط المستودع</Label>
        <div className="flex gap-1">
          <Input readOnly dir="ltr" value={link} className="h-9 text-xs" data-testid="wh-link-field" />
          <Button size="sm" variant="outline" onClick={() => copy(link, "الرابط")} className="h-9 shrink-0"><Copy className="h-4 w-4" /></Button>
        </div>
      </div>
      {password && (
        <div className="space-y-1">
          <Label className="text-xs">كلمة المرور (تظهر مرة واحدة)</Label>
          <div className="flex gap-1">
            <Input readOnly dir="ltr" value={password} className="h-9 font-mono-nums font-bold" data-testid="wh-password-field" />
            <Button size="sm" variant="outline" onClick={() => copy(password, "كلمة المرور")} className="h-9 shrink-0"><Copy className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      <Button onClick={download} variant="outline" className="w-full gap-1.5 border-emerald-300 text-emerald-700" data-testid="wh-download-card">
        <Download className="h-4 w-4" /> تنزيل بطاقة الدخول
      </Button>
    </div>
  );
};

const emptyForm = { name: "", city: "", manager_name: "", manager_id_number: "", password: "" };

export const WarehousesDialog = ({ open, onOpenChange, onChanged }) => {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [people, setPeople] = useState([]);
  const [personName, setPersonName] = useState("");
  const [personId, setPersonId] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {wh, password}

  const load = useCallback(async () => {
    try { setList((await api.warehouses()).items || []); } catch { toast.error("تعذّر التحميل"); }
  }, []);
  useEffect(() => { if (open) { load(); setCreated(null); setForm(emptyForm); setPeople([]); } }, [open, load]);

  const addPerson = () => {
    if (!personName.trim()) return;
    setPeople((p) => [...p, { name: personName.trim(), id_number: personId.trim() }]);
    setPersonName(""); setPersonId("");
  };

  const create = async () => {
    if (!form.name.trim()) { toast.warning("أدخل اسم المستودع"); return; }
    setBusy(true);
    try {
      const res = await api.createWarehouse({ ...form, people });
      setCreated({ wh: res, password: res.password });
      setForm(emptyForm); setPeople([]);
      load(); onChanged?.();
      toast.success("تم إنشاء المستودع");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإنشاء"); }
    finally { setBusy(false); }
  };

  const resetPw = async (w) => {
    try { const r = await api.resetWarehousePassword(w.id); setCreated({ wh: w, password: r.password }); toast.success("تم توليد كلمة مرور جديدة"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر"); }
  };
  const del = async (w) => {
    if (!window.confirm(`حذف فرع «${w.name}» وكل قطعه وسجلاته؟`)) return;
    try { await api.deleteWarehouse(w.id); toast.success("تم الحذف"); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="warehouses-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><Warehouse className="h-5 w-5 text-blue-600" /> إدارة المستودعات والفروع</DialogTitle>
          <DialogDescription>لكل فرع رصيده الخاص ورابط دخول مستقل (سحب فقط). المستودع الرئيسي يملك السحب والإدخال.</DialogDescription>
        </DialogHeader>

        {created && <CredentialCard wh={created.wh} password={created.password} onClose={() => setCreated(null)} />}

        {/* Existing warehouses */}
        <div className="space-y-2">
          {!list ? (
            <div className="py-8 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          ) : list.map((w) => (
            <div key={w.id} data-testid={`wh-row-${w.id}`} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                  {w.is_main ? <Building2 className="h-4 w-4 text-slate-500" /> : <Warehouse className="h-4 w-4 text-blue-500" />}
                  {w.name}
                  {w.is_main ? <Badge variant="secondary" className="text-[10px]">رئيسي</Badge>
                    : <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-600">فرع</Badge>}
                  {w.low_count > 0 && <Badge variant="outline" className="text-[10px] border-amber-200 bg-amber-50 text-amber-700">{w.low_count} منخفضة</Badge>}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {w.city || "—"} • {w.part_count ?? 0} قطعة{w.manager_name ? ` • المسؤول: ${w.manager_name}` : ""}
                </div>
              </div>
              {!w.is_main && (
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`wh-link-${w.id}`} title="نسخ الرابط" onClick={() => { navigator.clipboard.writeText(linkFor(w.access_token)); toast.success("تم نسخ الرابط"); }} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"><Link2 className="h-4 w-4" /></button>
                  <button data-testid={`wh-card-${w.id}`} title="بطاقة الدخول والباركود" onClick={() => setCreated({ wh: w, password: null })} className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100"><QrCode className="h-4 w-4" /></button>
                  <button data-testid={`wh-resetpw-${w.id}`} title="كلمة مرور جديدة" onClick={() => resetPw(w)} className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50"><KeyRound className="h-4 w-4" /></button>
                  <button data-testid={`wh-del-${w.id}`} title="حذف" onClick={() => del(w)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Create new branch */}
        <div className="rounded-2xl border border-dashed border-slate-300 p-4 space-y-3">
          <h4 className="font-heading font-bold text-slate-700 flex items-center gap-1.5"><Plus className="h-4 w-4" /> إضافة فرع مستودع</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">اسم المستودع</Label>
              <Input data-testid="wh-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">المدينة</Label>
              <Input data-testid="wh-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">الشخص المسؤول</Label>
              <Input data-testid="wh-manager" value={form.manager_name} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">رقم هوية المسؤول</Label>
              <Input data-testid="wh-manager-id" dir="ltr" value={form.manager_id_number} onChange={(e) => setForm({ ...form, manager_id_number: e.target.value })} /></div>
            <div className="space-y-1 sm:col-span-2"><Label className="text-xs">كلمة المرور (اتركها فارغة لتوليد تلقائي)</Label>
              <Input data-testid="wh-password" dir="ltr" inputMode="numeric" maxLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="تلقائي (6 أرقام)" /></div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">الأشخاص المصرّح لهم (يشتركون في نفس رابط الفرع)</Label>
            {people.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {people.map((p, i) => (
                  <Badge key={i} variant="secondary" className="gap-1">{p.name}{p.id_number ? ` (${p.id_number})` : ""}
                    <button onClick={() => setPeople((arr) => arr.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex gap-1.5">
              <Input data-testid="wh-person-name" placeholder="الاسم" value={personName} onChange={(e) => setPersonName(e.target.value)} className="h-9" />
              <Input data-testid="wh-person-id" dir="ltr" placeholder="الهوية" value={personId} onChange={(e) => setPersonId(e.target.value)} className="h-9 w-32" />
              <Button type="button" variant="outline" onClick={addPerson} className="h-9 shrink-0" data-testid="wh-add-person"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          <Button data-testid="wh-create-btn" onClick={create} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} إنشاء الفرع
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
